import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Segmented,
  Button,
  List,
  Badge,
  Pagination,
  Empty,
  Spin,
  Modal,
  message,
  Space,
  Tag,
} from 'antd';
import {
  BellOutlined,
  FileTextOutlined,
  SearchOutlined,
  BulbOutlined,
  DollarOutlined,
  TeamOutlined,
  SendOutlined,
  MessageOutlined,
  NotificationOutlined,
  CheckOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Notification, GetNotificationsParams } from '../../types/notification';
import { notificationApi } from '../../services/notification';
import { PRIORITY_MAP } from '../../constants/statusMappings';
import { COLORS } from '../../constants/colors';

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

// 通知类型图标映射
const notificationTypeIcons: Record<string, React.ReactNode> = {
  proposal_created: <FileTextOutlined />,
  requirement_analyzed: <SearchOutlined />,
  insight_generated: <BulbOutlined />,
  pricing_generated: <DollarOutlined />,
  references_ready: <TeamOutlined />,
  campaign_sent: <SendOutlined />,
  campaign_replied: <MessageOutlined />,
  system_announcement: <NotificationOutlined />,
  default: <BellOutlined />,
};

// 通知类型名称映射
const notificationTypeNames: Record<string, string> = {
  proposal_created: '提案创建',
  requirement_analyzed: '需求分析',
  insight_generated: '洞察生成',
  pricing_generated: '定价生成',
  references_ready: '参考准备',
  campaign_sent: '邀约发送',
  campaign_replied: '邀约回复',
  system_announcement: '系统公告',
};


// 获取相对时间
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return '刚刚';
  if (diffMinutes < 60) return `${diffMinutes}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays < 7) return `${diffDays}天前`;
  return date.toLocaleDateString('zh-CN');
};

export const NotificationList: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // 加载通知列表
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params: GetNotificationsParams = {
        page,
        page_size: pageSize,
      };
      if (filter === 'unread') {
        params.is_read = false;
      } else if (filter === 'read') {
        params.is_read = true;
      }

      const response = await notificationApi.getNotifications(params);
      // 假设 API 返回的数据结构
      const data = response.data as { items: Notification[]; total: number };
      setNotifications(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      message.error('获取通知列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filter]);

  // 标记已读
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(
        notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      message.success('已标记为已读');
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 全部标记已读
  const handleMarkAllAsRead = async () => {
    confirm({
      title: '确认操作',
      icon: <ExclamationCircleOutlined />,
      content: '确定要将所有通知标记为已读吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await notificationApi.markAllAsRead();
          setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
          message.success('已全部标记为已读');
        } catch (error) {
          message.error('操作失败');
        }
      },
    });
  };

  // 删除通知
  const handleDelete = async (notificationId: string) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: '确定要删除这条通知吗？',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await notificationApi.deleteNotification(notificationId);
          setNotifications(notifications.filter((n) => n.id !== notificationId));
          setTotal((prev) => prev - 1);
          message.success('删除成功');
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 点击通知
  const handleNotificationClick = async (notification: Notification) => {
    // 展开内容
    if (expandedIds.has(notification.id)) {
      setExpandedIds(
        new Set([...expandedIds].filter((id) => id !== notification.id))
      );
    } else {
      setExpandedIds(new Set([...expandedIds, notification.id]));
    }

    // 如果未读，标记已读
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
  };

  // 跳转到操作链接
  const handleNavigate = (notification: Notification) => {
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  // 分页改变
  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* 标题和操作栏 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            <BellOutlined style={{ marginRight: 8 }} />
            通知中心
          </Title>

          <Space size="middle">
            <Segmented
              value={filter}
              onChange={(value) => {
                setFilter(value as 'all' | 'unread' | 'read');
                setPage(1);
              }}
              options={[
                { label: '全部', value: 'all' },
                { label: '未读', value: 'unread' },
                { label: '已读', value: 'read' },
              ]}
            />
            <Button
              type="primary"
              ghost
              icon={<CheckOutlined />}
              onClick={handleMarkAllAsRead}
            >
              全部标为已读
            </Button>
          </Space>
        </div>

        {/* 通知列表 */}
        <Spin spinning={loading}>
          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filter === 'all' ? '暂无通知' : filter === 'unread' ? '暂无未读通知' : '暂无已读通知'}
              style={{ padding: '60px 0' }}
            />
          ) : (
            <List
              dataSource={notifications}
              renderItem={(item) => {
                const isExpanded = expandedIds.has(item.id);
                return (
                  <List.Item
                    style={{
                      padding: '16px 0',
                      backgroundColor: item.is_read ? 'transparent' : COLORS.bgLight,
                      borderBottom: `1px solid ${COLORS.gray200}`,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 16,
                      }}
                    >
                      {/* 图标 */}
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: COLORS.bgPrimaryLight,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: COLORS.primary,
                          fontSize: 18,
                        }}
                      >
                        {notificationTypeIcons[item.type] || notificationTypeIcons.default}
                      </div>

                      {/* 内容区域 */}
                      <div
                        style={{ flex: 1, minWidth: 0 }}
                        onClick={() => handleNotificationClick(item)}
                      >
                        {/* 标题行 */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 4,
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: 15,
                              color: item.is_read ? COLORS.gray500 : COLORS.gray600,
                            }}
                          >
                            {item.title}
                          </Text>
                          {!item.is_read && (
                            <Badge dot style={{ backgroundColor: COLORS.primary }} />
                          )}
                          <Tag
                            color={PRIORITY_MAP[item.priority]}
                            style={{ marginLeft: 'auto', fontSize: 11 }}
                          >
                            {notificationTypeNames[item.type] || '通知'}
                          </Tag>
                        </div>

                        {/* 内容 */}
                        <Paragraph
                          ellipsis={{ rows: isExpanded ? 0 : 2, expandable: false }}
                          style={{
                            margin: '0 0 8px 0',
                            fontSize: 14,
                            color: COLORS.gray400,
                          }}
                        >
                          {item.content}
                        </Paragraph>

                        {/* 底部信息 */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            fontSize: 12,
                            color: COLORS.textSecondary,
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {getRelativeTime(item.created_at)}
                          </Text>
                          {item.action_url && (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto', fontSize: 12 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigate(item);
                              }}
                            >
                              查看详情
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* 操作按钮 */}
                      <Space style={{ flexShrink: 0 }}>
                        {!item.is_read && (
                          <Button
                            type="text"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(item.id);
                            }}
                            title="标记已读"
                          />
                        )}
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          title="删除"
                        />
                      </Space>
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </Spin>

        {/* 分页 */}
        {total > 0 && (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `共 ${total} 条`}
              onChange={handlePageChange}
              pageSizeOptions={['10', '20', '50']}
            />
          </div>
        )}
      </Card>
    </div>
  );
};
